import { useState, useCallback } from 'react';
import api from '../utils/api';

export const useBatchLoader = () => {
    const [loadingProgress, setLoadingProgress] = useState({
        isLoading: false,
        isCompleted: false,
        currentBatch: 0,
        totalBatches: 0,
        loadedItems: 0,
        totalItems: 0,
        error: null
    });

    const resetProgress = useCallback(() => {
        setLoadingProgress({
            isLoading: false,
            isCompleted: false,
            currentBatch: 0,
            totalBatches: 0,
            loadedItems: 0,
            totalItems: 0,
            error: null
        });
    }, []);

    const batchLoadItems = useCallback(async (
        itemIds, 
        itemType = 'orders', 
        batchSize = 100,
        settings = {}
    ) => {
        const totalItems = itemIds.length;
        const optimizeMode = settings?.bulk_edit_optimize_mode === true || settings?.bulk_edit_optimize_mode === "1" || settings?.bulk_edit_optimize_mode === 1;
        const threshold = parseInt(settings?.bulk_edit_threshold) || 1000;
        const shouldUseBatchLoading = optimizeMode && totalItems > threshold;

        console.log('🔍 Batch Loading Decision:', {
            totalItems,
            itemType,
            batchSize,
            settings,
            optimizeMode,
            threshold,
            shouldUseBatchLoading
        });

        // If not using batch loading or small dataset, load normally
        if (!shouldUseBatchLoading) {
            console.log(`📦 Loading ${totalItems} ${itemType} normally (below threshold or optimize mode off)`);
            
            if (itemType === 'orders') {
                const promises = itemIds.map(id => api.getOrder(id));
                const responses = await Promise.all(promises);
                return responses.filter(r => r.success).map(r => r.order);
            } else {
                const promises = itemIds.map(id => api.getProduct(id));
                const responses = await Promise.all(promises);
                return responses.filter(r => r.success).map(r => r.product);
            }
        }

        // Use batch loading for large datasets
        console.log(`🚀 Using batch loading for ${totalItems} ${itemType} (above threshold and optimize mode on)`);
        
        const totalBatches = Math.ceil(totalItems / batchSize);

        setLoadingProgress({
            isLoading: true,
            isCompleted: false,
            currentBatch: 0,
            totalBatches,
            loadedItems: 0,
            totalItems,
            error: null
        });

        try {
            let allItems = [];
            let loadedItems = 0;

            for (let i = 0; i < totalBatches; i++) {
                const startIndex = i * batchSize;
                const endIndex = Math.min(startIndex + batchSize, totalItems);
                const batchIds = itemIds.slice(startIndex, endIndex);

                console.log(`📦 Loading batch ${i + 1}/${totalBatches}: ${itemType} ${startIndex}-${endIndex-1}`);

                setLoadingProgress(prev => ({
                    ...prev,
                    currentBatch: i + 1
                }));

                // Call the appropriate batch loading API
                let response;
                if (itemType === 'orders') {
                    response = await api.batchGetOrders(batchIds, batchSize);
                } else {
                    response = await api.batchGetProducts(batchIds, batchSize);
                }

                if (!response.success) {
                    throw new Error(response.message || `Failed to load ${itemType} batch ${i + 1}`);
                }

                allItems = allItems.concat(response[itemType] || []);
                loadedItems += response.processed_count || batchIds.length;

                setLoadingProgress(prev => ({
                    ...prev,
                    loadedItems
                }));

                console.log(`✅ Batch ${i + 1} loaded. Progress: ${loadedItems}/${totalItems}`);

                // Small delay between batches
                if (i < totalBatches - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            setLoadingProgress(prev => ({
                ...prev,
                isLoading: false,
                isCompleted: true
            }));

            console.log(`🎉 Batch loading completed: ${allItems.length} ${itemType} loaded`);
            return allItems;

        } catch (error) {
            setLoadingProgress(prev => ({
                ...prev,
                isLoading: false,
                error: error.message
            }));
            throw error;
        }
    }, []);

    const shouldUseBatchLoading = useCallback((itemCount, settings) => {
        const optimizeMode = settings?.bulk_edit_optimize_mode === true || settings?.bulk_edit_optimize_mode === "1" || settings?.bulk_edit_optimize_mode === 1;
        const threshold = parseInt(settings?.bulk_edit_threshold) || 1000;
        
        console.log('🔍 Batch Loading Check:', {
            itemCount,
            optimizeMode,
            threshold,
            settings: settings,
            shouldUseBatch: optimizeMode && itemCount > threshold
        });
        
        return optimizeMode && itemCount > threshold;
    }, []);

    return {
        loadingProgress,
        batchLoadItems,
        resetProgress,
        shouldUseBatchLoading
    };
};

export default useBatchLoader;